import { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import type { SurveyDetail } from '@/types'

interface SurveyLinkBuilderProps {
  survey: SurveyDetail
  baseUrl: string
}

export default function SurveyLinkBuilder({ survey, baseUrl }: SurveyLinkBuilderProps) {
  const [facilitatorToken, setFacilitatorToken] = useState('')
  const [group, setGroup] = useState('')
  const [session, setSession] = useState('')
  const [copied, setCopied] = useState(false)
  const [showQr, setShowQr] = useState(false)

  // Build the URL from selections
  const params = new URLSearchParams()
  if (facilitatorToken) params.set('f', facilitatorToken)
  if (group.trim()) params.set('g', group.trim())
  if (session.trim()) params.set('s', session.trim())

  const queryString = params.toString()
  const surveyUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl

  // Find facilitator name for display
  const selectedFacilitator = survey.survey_facilitators.find(sf => sf.token === facilitatorToken)

  function handleCopy() {
    navigator.clipboard.writeText(surveyUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function handleDownloadQr() {
    const svg = document.getElementById('link-builder-qr')
    if (!svg) return
    const canvas = document.createElement('canvas')
    canvas.width = 1024
    canvas.height = 1024
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const svgData = new XMLSerializer().serializeToString(svg)
    const img = new Image()
    img.onload = () => {
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, 1024, 1024)
      ctx.drawImage(img, 0, 0, 1024, 1024)
      const a = document.createElement('a')
      const parts = [survey.name]
      if (selectedFacilitator) parts.push(selectedFacilitator.name)
      if (group.trim()) parts.push(group.trim())
      if (session.trim()) parts.push(session.trim())
      a.download = parts.join(' - ').replace(/[^a-z0-9 -]/gi, '_').toLowerCase() + '_qr.png'
      a.href = canvas.toDataURL('image/png')
      a.click()
    }
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
  }

  return (
    <div className="card bg-base-100">
      <div className="card-body space-y-4">
        <h2 className="text-lg font-semibold">Share survey</h2>

        {!survey.published_at && (
          <p className="text-sm text-base-content/50">Publish the survey to generate shareable links.</p>
        )}

        {survey.published_at && (
          <>
            {/* Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Facilitator */}
              <fieldset className="fieldset">
                <legend className="fieldset-legend">Facilitator</legend>
                <select
                  className="select select-sm w-full"
                  value={facilitatorToken}
                  onChange={(e) => setFacilitatorToken(e.target.value)}
                >
                  <option value="">None</option>
                  {survey.survey_facilitators.map((sf) => (
                    <option key={sf.token} value={sf.token}>{sf.name}</option>
                  ))}
                </select>
              </fieldset>

              {/* Group */}
              <fieldset className="fieldset">
                <legend className="fieldset-legend">Group</legend>
                <input
                  type="text"
                  className="input input-sm w-full"
                  value={group}
                  onChange={(e) => setGroup(e.target.value)}
                  placeholder="e.g., Cohort 3, London"
                  list="existing-groups"
                />
                {survey.existing_groups.length > 0 && (
                  <datalist id="existing-groups">
                    {survey.existing_groups.map((g) => (
                      <option key={g} value={g} />
                    ))}
                  </datalist>
                )}
              </fieldset>

              {/* Session */}
              <fieldset className="fieldset">
                <legend className="fieldset-legend">Session</legend>
                <input
                  type="text"
                  className="input input-sm w-full"
                  value={session}
                  onChange={(e) => setSession(e.target.value)}
                  placeholder="e.g., Day 1, Module 3"
                  list="existing-sessions"
                />
                {survey.existing_sessions.length > 0 && (
                  <datalist id="existing-sessions">
                    {survey.existing_sessions.map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                )}
              </fieldset>
            </div>

            {/* Generated URL */}
            <div className="bg-base-200 rounded-lg p-3">
              <code className="text-xs break-all text-base-content/70">{surveyUrl}</code>
            </div>

            {/* Tags showing what's active */}
            {(facilitatorToken || group.trim() || session.trim()) && (
              <div className="flex flex-wrap gap-1">
                {selectedFacilitator && (
                  <span className="badge badge-soft badge-sm badge-primary">
                    Facilitator: {selectedFacilitator.name}
                  </span>
                )}
                {group.trim() && (
                  <span className="badge badge-soft badge-sm badge-info">
                    Group: {group.trim()}
                  </span>
                )}
                {session.trim() && (
                  <span className="badge badge-soft badge-sm badge-warning">
                    Session: {session.trim()}
                  </span>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <button onClick={handleCopy} className="btn btn-sm btn-primary">
                <span className={`iconify ${copied ? 'lucide--check' : 'lucide--clipboard'} size-4`} />
                {copied ? 'Copied' : 'Copy link'}
              </button>
              <button onClick={() => setShowQr(!showQr)} className="btn btn-sm btn-ghost">
                <span className="iconify lucide--qr-code size-4" />
                {showQr ? 'Hide QR' : 'Show QR'}
              </button>
            </div>

            {/* QR Code */}
            {showQr && (
              <div className="flex flex-col items-center gap-3 pt-4 border-t border-base-300/50">
                <QRCodeSVG id="link-builder-qr" value={surveyUrl} size={200} level="M" marginSize={2} />
                <button onClick={handleDownloadQr} className="btn btn-sm btn-ghost">
                  <span className="iconify lucide--download size-4" />
                  Download PNG
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
